import { Switch } from '@headlessui/react';
import classNames from 'classnames';
import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@repo/ux/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/ux/dialog';
import { Button } from '@/ui/button';

import { useCookieContext } from './CookieProvider';

function NecessaryCookieOption() {
  return <div className="text-xs">Always Enabled</div>;
}

function NonNecessaryCookieOption() {
  const { optionalCookies, setOptionalCookies } = useCookieContext();

  return (
    <div className="flex items-center">
      <span className="mr-3 text-xs">
        {optionalCookies ? 'Enabled' : 'Disabled'}
      </span>
      <Switch
        checked={optionalCookies}
        className={`${optionalCookies ? 'bg-green-600' : 'bg-gray-400'} relative inline-flex h-5 w-10 items-center rounded-full`}
        onChange={setOptionalCookies}
      >
        <span className="sr-only">
          {optionalCookies ? 'Enabled' : 'Disabled'}
        </span>
        <span
          className={`${
            optionalCookies ? 'translate-x-6' : 'translate-x-1'
          } inline-block h-4 w-4 transform rounded-full bg-white transition`}
        />
      </Switch>
    </div>
  );
}

function AnalyticsCookieOption() {
  const { analyticsConsent, setAnalyticsConsent } = useCookieContext();

  return (
    <div className="flex items-center">
      <span className="mr-3 text-xs">
        {analyticsConsent ? 'Enabled' : 'Disabled'}
      </span>
      <Switch
        checked={analyticsConsent}
        className={`${analyticsConsent ? 'bg-green-600' : 'bg-gray-400'} relative inline-flex h-5 w-10 items-center rounded-full`}
        onChange={setAnalyticsConsent}
      >
        <span className="sr-only">
          {analyticsConsent ? 'Enabled' : 'Disabled'}
        </span>
        <span
          className={`${
            analyticsConsent ? 'translate-x-6' : 'translate-x-1'
          } inline-block h-4 w-4 transform rounded-full bg-white transition`}
        />
      </Switch>
    </div>
  );
}

interface CookieSettingsModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

function CookieSettingsModal(props: CookieSettingsModalProps) {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const {
    optionalCookies,
    analyticsConsent,
    setCookieSettings,
    setBannerVisible,
  } = useCookieContext();

  const saveSettings = () => {
    setCookieSettings({
      enableOptionalCookies: optionalCookies,
      enableAnalytics: analyticsConsent,
    });
    setBannerVisible(false);
    props.setIsOpen(false);
  };

  return (
    <Dialog open={props.isOpen} onOpenChange={props.setIsOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Privacy Overview</DialogTitle>
        </DialogHeader>
        <p
          className={classNames(
            'mb-2 text-sm',
            isDescriptionExpanded ? 'line-clamp-none' : 'line-clamp-3'
          )}
        >
          This website uses cookies and analytics to improve your experience
          while you navigate through the website. Out of these, the cookies that
          are categorized as necessary are stored on your browser as they are
          essential for the working of basic functionalities of the website. We
          also use third-party cookies and analytics that help us analyze and
          understand how you use this website. These cookies and analytics will
          be stored in your browser only with your consent. You also have the
          option to opt-out of these cookies. But opting out of some of these
          cookies may affect your browsing experience.
        </p>

        <button
          className="mb-6 text-xs underline"
          onClick={() => {
            setIsDescriptionExpanded((prev) => !prev);
          }}
        >
          {isDescriptionExpanded ? 'Show less' : 'Show more'}
        </button>

        <Accordion type="single" collapsible className="mb-2 text-sm">
          <AccordionItem value="necessary">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="hover:cursor-pointer flex-1">
                <div>Necessary</div>
              </AccordionTrigger>
              <div className="pr-4">
                <NecessaryCookieOption />
              </div>
            </div>
            <AccordionContent>
              <p className="px-4 py-2">
                Necessary cookies are absolutely essential for the website to
                function properly. This category only includes cookies that
                ensures basic functionalities and security features of the
                website. These cookies do not store any personal information.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Accordion type="single" collapsible className="mb-2 text-sm">
          <AccordionItem value="non-necessary">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="hover:cursor-pointer flex-1">
                <div>Non-Necessary</div>
              </AccordionTrigger>
              <div className="pr-4" onClick={(e) => e.stopPropagation()}>
                <NonNecessaryCookieOption />
              </div>
            </div>
            <AccordionContent>
              <p className="px-4 py-2">
                Any cookies that may not be particularly necessary for the
                website to function and is used specifically to collect user
                personal data via analytics, ads, other embedded contents are
                termed as non-necessary cookies. It is mandatory to procure user
                consent prior to running these cookies on your website.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Accordion type="single" collapsible className="mb-2 text-sm">
          <AccordionItem value="analytics">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="hover:cursor-pointer flex-1">
                <div>Analytics</div>
              </AccordionTrigger>
              <div className="pr-4" onClick={(e) => e.stopPropagation()}>
                <AnalyticsCookieOption />
              </div>
            </div>
            <AccordionContent>
              <p className="px-4 py-2">
                Analytics cookies help us understand how visitors interact with
                our website by collecting and reporting information anonymously.
                This helps us improve our website and provide a better user
                experience. We use Google Analytics to track page views, user
                interactions, and performance metrics.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="flex justify-end">
          <Button
            className="mt-8 bg-green-600 p-4 px-3.8 py-2.5 text-sm hover:cursor-pointer hover:bg-green-500"
            onClick={saveSettings}
          >
            SAVE & ACCEPT
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CookieSettingsModal;
